import React from 'react';

const GENDER_CONFIG = {
  masculine: { color: 'var(--der)', shape: 'square' },
  feminine:  { color: 'var(--die)', shape: 'circle' },
  neuter:    { color: 'var(--das)', shape: 'triangle' },
};

const ARTICLE_TO_GENDER = {
  der: 'masculine',
  die: 'feminine',
  das: 'neuter',
};

/**
 * Renders a geometric genus marker: blue square (der), rose circle (die), green triangle (das).
 * Props:
 *   gender  — 'masculine' | 'feminine' | 'neuter'
 *   article — 'der' | 'die' | 'das' (alternative to gender)
 *   size    — pixel size (default 16)
 *   filled  — solid fill (default true); false = outline only
 *   className — extra wrapper classes
 */
const GenusShape = ({ gender, article, size = 16, filled = true, className = '' }) => {
  const resolvedGender = gender || ARTICLE_TO_GENDER[(article || '').toLowerCase()];
  const config = GENDER_CONFIG[resolvedGender] || GENDER_CONFIG.neuter;
  const { color, shape } = config;

  const fill = filled ? color : 'none';
  const stroke = color;
  const strokeWidth = filled ? 0 : 2;

  let shapeElement;
  if (shape === 'square') {
    const inset = filled ? 1 : 2;
    shapeElement = (
      <rect
        x={inset} y={inset}
        width={size - inset * 2} height={size - inset * 2}
        fill={fill} stroke={stroke} strokeWidth={strokeWidth}
      />
    );
  } else if (shape === 'circle') {
    const r = (size / 2) - (filled ? 1 : 2);
    shapeElement = (
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill={fill} stroke={stroke} strokeWidth={strokeWidth}
      />
    );
  } else {
    // triangle pointing up
    const pad = filled ? 1 : 2.5;
    const points = `${size / 2},${pad} ${size - pad},${size - pad} ${pad},${size - pad}`;
    shapeElement = (
      <polygon
        points={points}
        fill={fill} stroke={stroke} strokeWidth={strokeWidth}
      />
    );
  }

  return (
    <svg
      className={`genus-marker ${className}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={resolvedGender || 'unknown gender'}
    >
      {shapeElement}
    </svg>
  );
};

export default GenusShape;
